import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ApiError,
  api,
  quarterLabel,
  slotLabel,
  type AcceptOutcome,
  type Candidate,
  type QuarterDraft as Draft,
  type QuarterProposal,
  type RequestSummary,
  type Site,
} from '../lib/api';
import { addDays, dayLabel } from '../lib/dates';
import { navigate } from '../lib/router';

/**
 * A drafted quarter: propose, review, accept (D42, steps 3 and 4).
 *
 * Two states, and the page says plainly which one it is in. With no
 * proposal kept, it shows what a draft WOULD look like — nothing stored,
 * nobody told, no week created. Once the office keeps one, the same
 * figures describe a stored proposal it can argue with: any position can
 * be changed, a position can be settled so a re-run leaves it alone, and
 * periods are accepted ONE AT A TIME.
 *
 * Accepting writes an ordinary draft week, nothing more. Finalizing and
 * publishing stay where they were — a person's decision on the week page.
 */
type Report = Omit<Draft, 'cells'>;
type Cell = QuarterProposal['cells'][number];

export function QuarterDraft({ quarter, sites, editedBy }: { quarter: string; sites: Site[]; editedBy: string | null }) {
  const [proposal, setProposal] = useState<QuarterProposal | null>(null);
  const [preview, setPreview] = useState<Draft | null>(null);
  const [asked, setAsked] = useState<RequestSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Record<string, AcceptOutcome>>({});
  // Which quarter is on screen right now. An accept or an edit that comes back after somebody
  // has stepped to the next quarter must not repaint the one they are looking at.
  const showing = useRef(quarter);
  showing.current = quarter;
  const stale = () => showing.current !== quarter;

  useEffect(() => {
    let ignore = false;
    setProposal(null);
    setPreview(null);
    setError(null);
    setOutcomes({});
    setLoading(true);
    api
      .quarterProposal(quarter)
      .then(async (p) => {
        if (ignore) return;
        if (p && p.quarter === quarter) {
          setProposal(p);
          return;
        }
        const d = await api.draftQuarter(quarter);
        if (!ignore && d.quarter === quarter) setPreview(d);
      })
      .catch((e: Error) => { if (!ignore) setError(e.message); })
      .finally(() => { if (!ignore) setLoading(false); });
    api.requestSummary(quarter).then((r) => { if (!ignore) setAsked(r); }).catch(() => { /* the overlay is a nicety */ });
    return () => { ignore = true; };
  }, [quarter]);

  const d: Report | null = proposal ?? preview;
  const cells: Cell[] = useMemo(
    () =>
      proposal
        ? proposal.cells
        : [
            ...(preview?.cells ?? []).map((c, i) => ({ id: `p${i}`, ...c, spansWeeks: Math.max(1, c.spansWeeks ?? 1), edited: false, pinned: false })),
            // A stored proposal holds a row for every position including the empty ones; the
            // unsaved preview keeps them in `open` instead. Without these the page would count
            // "19 of 19 filled" on a quarter that is 19 of 116.
            ...(preview?.open ?? []).map((o, i) => ({
              id: `o${i}`,
              siteId: o.siteId,
              positionSlot: o.positionSlot,
              week: o.week,
              spansWeeks: Math.max(1, o.spansWeeks ?? 1),
              physicianId: null,
              edited: false,
              pinned: false,
            })),
          ],
    [proposal, preview],
  );

  const letterOf = useMemo(() => new Map(sites.map((s) => [s.id, s.letter])), [sites]);
  const nameOf = useMemo(() => new Map((d?.sites ?? []).map((s) => [s.siteId, s.siteName])), [d]);
  const wantedBy = useMemo(() => {
    const m = new Map<string, { wanted: Set<string>; blackout: Set<string> }>();
    for (const p of asked?.physicians ?? []) {
      if (p.request) m.set(p.abbreviation, { wanted: new Set(p.request.wantedWeeks), blackout: new Set(p.request.blackoutWeeks) });
    }
    return m;
  }, [asked]);

  /** Who is where, week by week — read from the live cells, so hand edits show. */
  const byPerson = useMemo(() => {
    const m = new Map<string, Map<string, { siteId: string; positionSlot: string }[]>>();
    for (const c of cells) {
      if (!c.physicianId || !d) continue;
      if (!m.has(c.physicianId)) m.set(c.physicianId, new Map());
      const start = d.weeks.indexOf(c.week);
      for (let k = 0; k < Math.max(1, c.spansWeeks ?? 1); k++) {
        const w = d.weeks[start + k];
        // A period running past the quarter's last week is still worked; the grid only has
        // columns for this quarter, so the row note carries the real total instead.
        // A LIST, not one entry: somebody put in two positions in the same week by hand is
        // exactly what this grid is for, and keeping only the last would hide it.
        if (w) m.get(c.physicianId)!.set(w, [...(m.get(c.physicianId)!.get(w) ?? []), { siteId: c.siteId, positionSlot: c.positionSlot }]);
      }
    }
    return m;
  }, [cells, d]);

  const periods = useMemo(() => {
    const m = new Map<string, { siteId: string; periodStart: string; cells: Cell[] }>();
    for (const c of cells) {
      const k = `${c.siteId}|${c.week}`;
      if (!m.has(k)) m.set(k, { siteId: c.siteId, periodStart: c.week, cells: [] });
      m.get(k)!.cells.push(c);
    }
    return [...m.values()].sort((a, b) => a.siteId.localeCompare(b.siteId) || a.periodStart.localeCompare(b.periodStart));
  }, [cells]);
  const bySite = useMemo(() => {
    const m = new Map<string, typeof periods>();
    for (const p of periods) m.set(p.siteId, [...(m.get(p.siteId) ?? []), p]);
    return [...m.entries()];
  }, [periods]);

  const weekAt = useMemo(
    () => new Map((proposal?.periods ?? []).map((w) => [`${w.siteId}|${w.periodStart}`, w])),
    [proposal],
  );
  const whyOpenAt = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of d?.open ?? []) m.set(`${o.siteId}|${o.positionSlot}|${o.week}`, whyOpen(o.reason));
    return m;
  }, [d]);
  const reasonAt = useMemo(() => new Map((d?.open ?? []).map((o) => [`${o.siteId}|${o.positionSlot}|${o.week}`, o.reason])), [d]);
  // The whole open cell, for the part-week offers hung off it (D60).
  const openAt = useMemo(() => new Map((d?.open ?? []).map((o) => [`${o.siteId}|${o.positionSlot}|${o.week}`, o])), [d]);

  // Whoever set the token clears it. A cell edit finishing must not put the accept buttons
  // back and drop the "Accepting…" label while that accept is still running.
  const done = (tag: string) => setBusy((b) => (b === tag ? null : b));

  const make = async (supersedes?: string) => {
    const tag = supersedes ? 'again' : 'keep';
    setBusy(tag);
    setError(null);
    try {
      const p = await api.createProposal({ quarter, supersedes: supersedes ?? null, createdBy: editedBy });
      if (stale()) return;
      setProposal(p);
      setPreview(null);
      setOutcomes({});
    } catch (e) {
      if (!stale()) setError(problem(e));
    } finally {
      done(tag);
    }
  };

  const changeCell = async (cellId: string, change: { physicianId?: string | null; pinned?: boolean }) => {
    if (!proposal) return;
    setBusy(cellId);
    setError(null);
    try {
      const updated = await api.setProposalCell(proposal.id, cellId, change);
      if (stale()) return;
      // From the current proposal, not the one this closure was made with: two cells can be
      // changed at once, and the second answer must not undo the first.
      setProposal((cur) => (cur ? { ...cur, cells: cur.cells.map((c) => (c.id === cellId ? { ...c, ...updated } : c)) } : cur));
    } catch (e) {
      if (!stale()) setError(problem(e));
    } finally {
      done(cellId);
    }
  };

  const accept = async (want: { siteId: string; periodStart: string }[], tag: string, replaceExisting = false) => {
    if (!proposal || !want.length) return;
    setBusy(tag);
    setError(null);
    try {
      const res = await api.acceptProposal(proposal.id, { periods: want, acceptedBy: editedBy, replaceExisting });
      const fresh = await api.proposalById(proposal.id);
      if (stale()) return;
      setOutcomes((o) => ({ ...o, ...Object.fromEntries(res.map((r) => [`${r.siteId}|${r.periodStart}`, r])) }));
      setProposal(fresh);
    } catch (e) {
      if (!stale()) setError(problem(e));
    } finally {
      done(tag);
    }
  };

  if (loading) return <p className="muted">Working out a draft…</p>;
  if (error && !d) return <p className="error">Couldn't draft that quarter ({error}).</p>;
  if (!d) return <p className="muted">Nothing to draft for {quarterLabel(quarter)}.</p>;

  const liveWeeks = (id: string) => byPerson.get(id)?.size ?? 0;
  // Rows come from the LIVE cells, not the drafter's own report. Put somebody in a position by
  // hand who the drafter gave nothing and they belong on the grid; edit somebody out of every
  // position and they do not. The report still supplies what it knows about each of them.
  const reported = new Map(d.people.map((p) => [p.physicianId, p]));
  const people = [...byPerson.keys()]
    .map((id) => reported.get(id) ?? { physicianId: id, weeks: 0, alreadyWorkingWeeks: 0, targetWeeks: null, wantedWeeksGiven: 0, wantedWeeksAsked: 0, longestRun: 0, flagged: false, sites: [] })
    .sort((a, b) => liveWeeks(b.physicianId) + b.alreadyWorkingWeeks - (liveWeeks(a.physicianId) + a.alreadyWorkingWeeks) || a.physicianId.localeCompare(b.physicianId));
  const edits = cells.filter((c) => c.edited).length;
  const openNow = cells.filter((c) => !c.physicianId).length;
  const underBound = d.filledOfThose < d.ceiling;

  return (
    <section className="qdraft">
      <div className="gridtools noprint">
        <div className="quarterpicker">
          <button type="button" onClick={() => navigate(`/admin/draft?quarter=${step(quarter, -1)}`)} aria-label="Previous quarter">‹</button>
          <h1>{quarterLabel(quarter)} — {proposal ? 'the proposed draft' : 'a proposed draft'}</h1>
          <button type="button" onClick={() => navigate(`/admin/draft?quarter=${step(quarter, 1)}`)} aria-label="Next quarter">›</button>
        </div>
        <button className="linklike" type="button" onClick={() => navigate(`/admin/grid?quarter=${quarter}`)}>the real grid</button>
        <button className="linklike" type="button" onClick={() => navigate(`/admin/requests?quarter=${quarter}`)}>requests</button>
      </div>

      {proposal ? (
        <p className="notice">
          <strong>This is a proposal, not a schedule.</strong> It is kept so you can change it, but no week exists until you accept one —
          and accepting only creates a <em>draft</em>. Nobody is told anything until a week is finalized and published on its own page.
          {' '}Drafted {new Date(proposal.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} by {proposal.createdBy}
          {proposal.supersedesId && <> · replaces an earlier draft</>}.
        </p>
      ) : (
        <p className="notice">
          <strong>Nothing here is saved.</strong> This is what a draft would look like — nothing is stored, nobody has been told, and no
          week has been created. Keep it if you want to work on it.
        </p>
      )}

      <div className="formactions noprint">
        {proposal ? (
          <button type="button" disabled={!!busy} onClick={() => void make(proposal.id)}>
            {busy === 'again' ? 'Drafting…' : 'Draft again, keeping what is settled'}
          </button>
        ) : (
          <button type="button" className="primary" disabled={!!busy} onClick={() => void make()}>
            {busy === 'keep' ? 'Keeping…' : 'Keep this as a proposal to work on'}
          </button>
        )}
        {error && <span className="error small">{error}</span>}
      </div>

      <div className="card">
        <h2>{cells.length - openNow} of {cells.length} positions filled</h2>
        <p className="muted small">
          across {d.sites.map((s) => s.siteName).join(', ')} · {people.length} {people.length === 1 ? 'physician' : 'physicians'} ·{' '}
          {underBound ? (
            <span className="warn">
              no arrangement could have covered more than {d.ceiling} of the {d.of} single-week positions, and this draft covered {d.filledOfThose} of them —
              so {d.ceiling - d.filledOfThose} more were within reach
            </span>
          ) : (
            <span className="ok">no arrangement could have covered more of the single-week positions than this one did</span>
          )}
        </p>
        <p className="muted small">
          <strong>Preference policy: {d.policy.name}.</strong> In order: {d.policy.order.join(' → ')}, then a rotation for whatever still
          ties. It honours the weeks people asked for; prefers not to run anybody past {d.policy.preferRunsAtMost} weeks back to back; and
          while somebody is on a run, keeps them in the same position. None of that evens the work out, because no fairness rule has been
          decided.
        </p>
        <p className="muted small">
          <strong>Silence is not availability.</strong> A physician who has not answered this quarter's request form is not offered any
          position at all — nobody is scheduled on an assumption. That is why an unanswered roster produces a page of open slots rather
          than a full quarter: the gaps below say how many of each is "never answered", and those are a phone call, not a staffing problem.
        </p>
        <p className="muted small">
          <strong>Nobody is capped.</strong> There is no limit on how many weeks a physician may work; what someone said they were aiming
          for is shown beside what they got, never used to refuse them. Running past {d.policy.preferRunsAtMost} weeks back to back is
          avoided where there is an alternative and flagged where there is not — it is a preference, not a rule. A physician's own stated
          "most weeks in a row" is their answer, and that one binds.
        </p>
        {people.length > 0 && <Spread people={people} liveWeeks={liveWeeks} policy={d.policy} />}
        {/* Hospitals fall out for different reasons — one has no policy, another's nights are out
            of scope — so each reason names its own hospitals rather than the first one speaking for all. */}
        {[...group(d.notDrafted, (n) => n.why)].map(([why, list]) => (
          <p className="muted small" key={why}>Left out of this draft: {list.map((n) => n.siteName).join(', ')} — {why}.</p>
        ))}
        {d.settledProblems?.length > 0 && (
          <p className="warn small">
            <strong>{d.settledProblems.length} settled {d.settledProblems.length === 1 ? 'position is' : 'positions are'} no longer possible.</strong>{' '}
            {d.settledProblems.map((sp) => `${sp.physicianId} at ${nameOf.get(sp.siteId) ?? sp.siteId} ${slotLabel(sp.positionSlot)}, week of ${short(sp.week)} — ${sp.why}`).join('; ')}
            . They have been left exactly where you put them, and accepting those periods will refuse until the reason is dealt with.
          </p>
        )}
        {edits > 0 && (
          <p className="warn small">
            {edits} {edits === 1 ? 'position has' : 'positions have'} been changed by hand since this was drafted. The counts and the grid
            show what is there now; the coverage bound, the run lengths and the flags describe the draft <em>as proposed</em>. Draft again,
            keeping what is settled, to bring those up to date.
          </p>
        )}
      </div>

      {openNow > 0 && (
        <div className="card">
          <h2>What is not filled</h2>
          <ul className="gaps">
            {/* From the LIVE cells: a position emptied by hand is a gap, and one filled by hand
                is not — quoting the drafter's own list would contradict the count above it. */}
            {[...group(cells.filter((c) => !c.physicianId), (c) => `${c.siteId}|${c.positionSlot}`)].map(([k, list]) => {
              const [siteId, positionSlot] = k.split('|');
              // The counts differ week by week, so they are summed across the group rather
              // than quoting the first week's reasons as if they explained all of them.
              const weeksMissing = list.reduce((n, c) => n + Math.max(1, c.spansWeeks ?? 1), 0);
              const reasons = list.map((c) => reasonAt.get(`${c.siteId}|${c.positionSlot}|${c.week}`)).filter(Boolean) as Draft['open'][number]['reason'][];
              const byHand = list.length - reasons.length;
              const none = reasons.length > 0 && reasons.every((r) => r.kind === 'nobody-credentialed');
              const tally = { neverAnswered: 0, saidNo: 0, elsewhere: 0, tooManyInARow: 0 };
              for (const r of reasons) {
                if (r.kind !== 'none-free') continue;
                tally.neverAnswered += r.neverAnswered;
                tally.saidNo += r.saidNo;
                tally.elsewhere += r.elsewhere;
                tally.tooManyInARow += r.tooManyInARow;
              }
              const parts = [
                tally.neverAnswered && `${tally.neverAnswered} × never answered the request form`,
                tally.saidNo && `${tally.saidNo} × said they can't work it`,
                tally.elsewhere && `${tally.elsewhere} × already at another hospital`,
                tally.tooManyInARow && `${tally.tooManyInARow} × would be too many weeks in a row`,
                byHand && `${byHand} emptied by hand`,
              ].filter(Boolean);
              // Part-week offers for these weeks (D60). The drafter never fills from one —
              // whole weeks come first — so this is the one place they surface: on the
              // position nobody could cover, for the office to take up or ignore.
              const offers = list.flatMap((c) => {
                const o = openAt.get(`${c.siteId}|${c.positionSlot}|${c.week}`);
                return (o?.partialOffers ?? []).map((x) => ({ week: c.week, ...x }));
              });
              return (
                <li key={k}>
                  <strong>{nameOf.get(siteId) ?? siteId} {slotLabel(positionSlot)}</strong> — {weeksMissing} {weeksMissing === 1 ? 'week' : 'weeks'}:{' '}
                  {none ? 'nobody is credentialed for it at all' : parts.length ? `across those weeks, ${parts.join(', ')}` : 'no reason recorded'}
                  {offers.length > 0 && (
                    <ul className="offers">
                      {offers.map((o, i) => (
                        <li key={`${o.week}|${o.abbreviation}|${i}`}>
                          <span className="pill st-partial">part week</span>{' '}
                          <strong>{o.firstName} {o.lastName}</strong> could cover {o.dates.length} day{o.dates.length === 1 ? '' : 's'} of the week of {dayLabel(o.week).replace(/^\w+, /, '')}
                          <span className="muted"> — {o.dates.map((d) => dayLabel(d).replace(/^(\w{3})\w*,.*?(\d+)(?:,.*)?$/, '$1 $2')).join(', ')}</span>
                          {o.note && <span className="muted"> · {o.note}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {proposal &&
        bySite.map(([siteId, list]) => {
          const mine = list.filter((p) => weekAt.get(`${siteId}|${p.periodStart}`)?.fromThisProposal).length;
          // A bulk accept only writes periods that have no week at all. Replacing a week
          // somebody else planned is a deliberate, one-at-a-time decision.
          const fresh = list.filter((p) => !weekAt.has(`${siteId}|${p.periodStart}`)).map((p) => ({ siteId, periodStart: p.periodStart }));
          const held = list.length - mine - fresh.length;
          return (
            <div className="card" key={siteId}>
              <div className="qsitehead">
                <h2>{nameOf.get(siteId) ?? siteId}</h2>
                <span className="muted small">
                  {mine} of {list.length} {list.length === 1 ? 'period' : 'periods'} accepted from this proposal
                  {held > 0 && <> · {held} already {held === 1 ? 'has a week' : 'have weeks'} of {held === 1 ? 'its' : 'their'} own</>}
                </span>
                <button type="button" className="primary" disabled={!!busy || !fresh.length} onClick={() => void accept(fresh, `site:${siteId}`)}>
                  {busy === `site:${siteId}` ? 'Accepting…' : fresh.length ? `Accept the ${fresh.length} with no week yet` : 'Nothing left to accept'}
                </button>
              </div>
              <p className="muted small">
                Each period is accepted on its own — one that cannot be written says why and the rest still go through. Accepting checks
                credentialing again at the moment of writing, and a physician who is not credentialed is refused outright. A period that
                already has a week is left alone here; replace it from its own card, one at a time.
              </p>
              <ul className="qperiods">
                {list.map((p) => (
                  <Period
                    key={p.periodStart}
                    siteId={siteId}
                    periodStart={p.periodStart}
                    cells={p.cells}
                    week={weekAt.get(`${siteId}|${p.periodStart}`) ?? null}
                    outcome={outcomes[`${siteId}|${p.periodStart}`] ?? null}
                    whyOpenAt={whyOpenAt}
                    busy={busy}
                    onChange={changeCell}
                    onAccept={(replaceExisting) => void accept([{ siteId, periodStart: p.periodStart }], `period:${siteId}|${p.periodStart}`, replaceExisting)}
                  />
                ))}
              </ul>
            </div>
          );
        })}

      <div className="card">
        <h2>The quarter</h2>
        <div className="gridscroll">
          <table className="grid">
            <thead>
              <tr>
                <th className="sticky name">Physician</th>
                {d.weeks.map((w) => <th key={w} className="week" scope="col"><div className="wk-days">{dayLabel(w).replace(/^\w+, /, '').replace(/, \d{4}$/, '')}</div></th>)}
                <th className="notes">Weeks</th>
              </tr>
            </thead>
            <tbody>
              {people.map((p) => {
                const said = wantedBy.get(p.physicianId);
                const mine = byPerson.get(p.physicianId);
                return (
                  <tr key={p.physicianId}>
                    <th className="sticky name">{p.physicianId}</th>
                    {d.weeks.map((w) => {
                      const at = mine?.get(w) ?? [];
                      // What they asked for, beside what they were given: green where a week they
                      // wanted was given, red where they are in a week they said they can't work
                      // OR in two positions at once, and a faint w where they asked and did not get it.
                      const kind = at.length > 1 ? ' clash' : at.length ? (said?.blackout.has(w) ? ' clash' : said?.wanted.has(w) ? ' wanted' : '') : said?.wanted.has(w) ? ' missed' : '';
                      return (
                        <td key={w} className={`cell${kind}`} title={titleFor(at, said, w)}>
                          {at.length
                            ? at.map((a, i) => (
                                <span key={i} className={`letter site-${a.siteId}`}>
                                  {letterOf.get(a.siteId) ?? a.siteId[0].toUpperCase()}
                                  {a.positionSlot.startsWith('rounder-') ? a.positionSlot.slice(-1) : ''}
                                </span>
                              ))
                            : said?.wanted.has(w)
                              ? <span className="askmark">w</span>
                              : null}
                        </td>
                      );
                    })}
                    <td className={`notes${p.flagged ? ' flagged' : ''}`}>
                      {liveWeeks(p.physicianId) + p.alreadyWorkingWeeks} {liveWeeks(p.physicianId) + p.alreadyWorkingWeeks === 1 ? 'week' : 'weeks'}
                      {p.alreadyWorkingWeeks > 0 && <span className="muted"> ({liveWeeks(p.physicianId)} new)</span>}
                      {p.targetWeeks !== null && <span className="muted"> · aiming for {p.targetWeeks}</span>}
                      {p.wantedWeeksAsked > 0 && <span className="muted"> · {said ? [...(mine?.keys() ?? [])].filter((w) => said.wanted.has(w)).length : p.wantedWeeksGiven}/{p.wantedWeeksAsked} asked</span>}
                      {p.longestRun > 1 && <span className="muted"> · {p.longestRun} in a row</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="legend muted">
          Each cell is the hospital's letter and the rounder number. Green is a week they asked for, red a week they said they cannot work,
          and a faint w a week they asked for and did not get.
        </p>
      </div>
    </section>
  );
}

/**
 * One hospital period: its positions, and the one button that writes it as a draft week.
 *
 * What the button says depends on what is already there. A period this proposal accepted
 * is done and points at its week. A period somebody else already planned is NOT quietly
 * replaced — a draft can be replaced deliberately from here, and a finalized, published or
 * imported week cannot be touched from a proposal at all.
 */
function Period({
  siteId,
  periodStart,
  cells,
  week,
  outcome,
  whyOpenAt,
  busy,
  onChange,
  onAccept,
}: {
  siteId: string;
  periodStart: string;
  cells: Cell[];
  week: QuarterProposal['periods'][number] | null;
  outcome: AcceptOutcome | null;
  whyOpenAt: Map<string, string>;
  busy: string | null;
  onChange: (cellId: string, change: { physicianId?: string | null; pinned?: boolean }) => Promise<void>;
  onAccept: (replaceExisting: boolean) => void;
}) {
  const span = Math.max(1, ...cells.map((c) => c.spansWeeks ?? 1));
  const open = cells.filter((c) => !c.physicianId).length;
  const tag = `period:${siteId}|${periodStart}`;
  const mine = !!week?.fromThisProposal;
  const imported = week?.source === 'imported';
  const replaceable = !!week && !mine && !imported && week.status === 'draft';
  const held = !!week && !mine && !replaceable;
  const link = (
    <button type="button" className="linklike" onClick={() => navigate(`/admin/sites/${siteId}/periods/${periodStart}`)}>open the week</button>
  );
  return (
    <li className={`qperiod${mine ? ' settled' : ''}${held ? ' held' : ''}`}>
      <div className="qperiodhead">
        <strong>
          {short(periodStart)}
          {span > 1 && <> – {short(addDays(periodStart, span * 7 - 1))}</>}
        </strong>
        {mine ? (
          <>
            <span className="pill tiny">accepted · {week!.status}</span>
            {link}
          </>
        ) : held ? (
          <>
            <span className="pill tiny">{imported ? `imported ${week!.status}` : `already ${week!.status}`}</span>
            {link}
          </>
        ) : (
          <>
            {open > 0 && <span className="warn small">{open} open</span>}
            <button type="button" disabled={!!busy} onClick={() => onAccept(replaceable)}>
              {busy === tag ? 'Accepting…' : replaceable ? 'Replace the draft' : 'Accept this period'}
            </button>
          </>
        )}
      </div>
      {held && (
        <p className="muted small">
          {imported
            ? 'Imported from QGenda — a proposal will not overwrite it.'
            : 'A proposal will not replace it. Change it shift by shift on its own week page, or reopen it there to plan it again.'}
        </p>
      )}
      {replaceable && <p className="warn small">There is already a draft for this period — accepting replaces its plan.</p>}
      <ul className="qcells">
        {cells.map((c) => (
          <CellRow
            key={c.id}
            siteId={siteId}
            cell={c}
            locked={mine || held}
            busy={!!busy}
            why={c.physicianId ? null : whyOpenAt.get(`${c.siteId}|${c.positionSlot}|${c.week}`) ?? null}
            onChange={onChange}
          />
        ))}
      </ul>
      {outcome && (
        <p className={`small ${outcome.status === 'accepted' ? 'ok' : outcome.status === 'refused' ? 'error' : 'warn'}`}>
          {outcome.status === 'accepted' ? '✓ ' : ''}{outcome.message}
          {outcome.errors?.length ? <> — {outcome.errors.join('; ')}</> : null}
        </p>
      )}
    </li>
  );
}

/**
 * One position. The picker is fetched only when somebody opens it: a quarter is
 * a hundred-odd positions, and asking the server who is eligible for every one
 * of them on page load would be a hundred-odd questions nobody asked.
 */
function CellRow({
  siteId,
  cell,
  locked,
  busy,
  why,
  onChange,
}: {
  siteId: string;
  cell: Cell;
  locked: boolean;
  busy: boolean;
  why: string | null;
  onChange: (cellId: string, change: { physicianId?: string | null; pinned?: boolean }) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  // The day after the handover is the day the holder is actually on the position.
  const cands = useCandidates(editing ? siteId : null, cell.positionSlot, addDays(cell.week, 1));
  return (
    <li className="qcell">
      <span className="slotname">{slotLabel(cell.positionSlot)}</span>
      {editing && !locked ? (
        <Picker
          label={`${slotLabel(cell.positionSlot)}, week of ${cell.week}`}
          cands={cands}
          value={cell.physicianId}
          onChange={(v) => { setEditing(false); void onChange(cell.id, { physicianId: v }); }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <span className={`chosen${cell.physicianId ? '' : ' open'}`}>{cell.physicianId ?? <em>open</em>}</span>
      )}
      {!locked && !editing && (
        <button type="button" className="linklike" disabled={busy} onClick={() => setEditing(true)}>change</button>
      )}
      {!locked && (
        <label className="pin small" title="Keep this one exactly as it is when the quarter is drafted again">
          <input type="checkbox" checked={cell.pinned} disabled={busy} onChange={(e) => void onChange(cell.id, { pinned: e.target.checked })} /> keep
        </label>
      )}
      {cell.pinned && <span className="pill tiny">settled</span>}
      {cell.edited && <span className="pill tiny">changed by hand</span>}
      {why && <span className="why muted">{why}</span>}
    </li>
  );
}

function Picker({
  label,
  cands,
  value,
  onChange,
  onCancel,
}: {
  label: string;
  cands: Candidate[] | null;
  value: string | null;
  onChange: (v: string | null) => void;
  onCancel: () => void;
}) {
  if (cands === null) return <span className="muted small">checking who's eligible…</span>;
  return (
    <span className="pick">
      <select
        aria-label={label}
        autoFocus
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">— leave open —</option>
        {cands.map((c) => (
          <option key={c.abbreviation} value={c.abbreviation} disabled={c.blockers.length > 0}>
            {c.lastName}, {c.firstName}{c.blockers.length ? ` — ${c.blockers[0]}` : c.notes.length ? ` (${c.notes[0]})` : ''}
          </option>
        ))}
        {/* Somebody already in the cell who is no longer eligible must stay visible rather than silently vanish. */}
        {value && !cands.some((c) => c.abbreviation === value) && <option value={value}>{value} — no longer eligible</option>}
      </select>
      <button type="button" className="linklike" onClick={onCancel}>cancel</button>
    </span>
  );
}

/** The spread, and how many people are worth a second look. */
function Spread({ people, liveWeeks, policy }: { people: Report['people']; liveWeeks: (id: string) => number; policy: Report['policy'] }) {
  const loads = people.map((p) => liveWeeks(p.physicianId) + p.alreadyWorkingWeeks).filter((n) => n > 0);
  if (!loads.length) return null;
  const flagged = people.filter((p) => p.flagged).length;
  return (
    <p className="muted small">
      <strong>The spread is {Math.min(...loads)} to {Math.max(...loads)} weeks</strong>
      {flagged > 0 ? (
        <>
          , and {flagged} of {people.length} {flagged === 1 ? 'physician is' : 'physicians are'} marked for a look — more than{' '}
          {policy.preferRunsAtMost} weeks back to back, or more than they asked for
        </>
      ) : (
        <>, and nobody is over {policy.preferRunsAtMost} weeks back to back or over what they asked for</>
      )}
      . Nothing here is evening the work out, because no fairness rule has been decided — that is the decision that narrows this spread.
    </p>
  );
}

function useCandidates(siteId: string | null, slot: string, date: string): Candidate[] | null {
  const [cands, setCands] = useState<Candidate[] | null>(null);
  useEffect(() => {
    if (!siteId) return;
    let ignore = false;
    setCands(null);
    api.candidates(siteId, slot, date).then((c) => { if (!ignore) setCands(c); }).catch(() => { if (!ignore) setCands([]); });
    return () => { ignore = true; };
  }, [siteId, slot, date]);
  return siteId ? cands : null;
}

function whyOpen(reason: Report['open'][number]['reason']): string {
  if (reason.kind === 'nobody-credentialed') return 'nobody is credentialed for it';
  const parts = [
    reason.neverAnswered && `${reason.neverAnswered} never answered`,
    reason.saidNo && `${reason.saidNo} said they can't`,
    reason.elsewhere && `${reason.elsewhere} already elsewhere`,
    reason.tooManyInARow && `${reason.tooManyInARow} would be too many in a row`,
  ].filter(Boolean);
  return parts.length ? `${reason.credentialed} credentialed, none free: ${parts.join(', ')}` : `none of the ${reason.credentialed} credentialed were free`;
}

function group<T>(items: T[], key: (item: T) => string): Map<string, T[]> {
  const m = new Map<string, T[]>();
  for (const i of items) m.set(key(i), [...(m.get(key(i)) ?? []), i]);
  return new Map([...m.entries()].sort(([a], [b]) => a.localeCompare(b)));
}

function titleFor(at: { siteId: string; positionSlot: string }[], said: { wanted: Set<string>; blackout: Set<string> } | undefined, week: string): string {
  const where = at.map((a) => `${a.siteId} · ${slotLabel(a.positionSlot)}`).join(' AND ');
  const asked = said?.wanted.has(week) ? 'asked for this week' : said?.blackout.has(week) ? "said they can't work this week" : '';
  return [at.length > 1 ? `in two positions at once — ${where}` : where, asked].filter(Boolean).join(' — ');
}

function short(iso: string): string {
  return dayLabel(iso).replace(/^\w+, /, '').replace(/, \d{4}$/, '');
}

function problem(e: unknown): string {
  return e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e);
}

function step(quarter: string, by: number): string {
  const [y, q] = quarter.split('-Q').map(Number);
  const n = (q - 1) + by;
  return `${y + Math.floor(n / 4)}-Q${((n % 4) + 4) % 4 + 1}`;
}
